// src/components/Utils.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Repeat, ArrowLeft, ChevronLeft, FileText, Code } from 'lucide-react';
import SimpleEditor from './Utils/SimpleEditor';
import MDEditor from './Utils/MDEditor';
import HTMLEditor from './Utils/HTMLEditor';
import MDtoHTMLConverter from './Utils/MDtoHTMLConverter';
import VisitStats from './VisitStats';
import './Utils.css';
import './Utils/common_Utils.css';

const Utils = ({ user, userProfile }) => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [initialFileContent, setInitialFileContent] = useState(null); // [수정] 파일 내용 상태 추가
  const navigate = useNavigate();
  const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isMobileDevice = isMobile();  

  // [추가] 모달 상태 관리
  const [modalMessage, setModalMessage] = useState(null);
  
  // [추가] 모달 닫기 핸들러
  const closeModal = () => {
    setModalMessage(null);
  };

  const tools = [
    {
      id: 'simpleeditor',
      icon: FileText,
      title: 'Simple Editor',
      description: '간단한 문서를 작성하고 실시간으로 미리보기할 수 있습니다.',
      component: SimpleEditor,
      accepts: ['.*'] // [수정] 허용 파일 확장자 추가
    },
    {
      id: 'markdown',
      icon: FileText,
      title: 'Markdown 편집기',
      description: 'Markdown 문서를 작성하고 실시간으로 미리보기할 수 있습니다.',
      component: MDEditor,
      accepts: ['.md', '.markdown', '.txt'] // [수정] 허용 파일 확장자 추가
    },
    {
      id: 'html',
      icon: Code,
      title: 'HTML 편집기',
      description: 'HTML 코드를 작성하고 실시간으로 결과를 확인할 수 있습니다.',
      component: HTMLEditor,
      accepts: ['.html', '.htm'] // [수정] 허용 파일 확장자 추가
    },
    {
      id: 'md-to-html',
      icon: Repeat,
      title: 'MD → HTML 변환기',
      description: 'Markdown 파일을 HTML 파일로 변환하고 다운로드할 수 있습니다.',
      component: MDtoHTMLConverter,
      accepts: ['.md', '.markdown', '.txt']
    }
  ];

  // 페이지 로드 시 스크롤을 맨 위로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // [수정] 파일 내용을 인수로 받을 수 있도록 handleToolClick 수정
  const handleToolClick = (tool, content = null) => {
    // if ((tool.id === 'markdown' || tool.id === 'html') && isMobileDevice) {
    //   setModalMessage("이 서비스는 데스크톱 PC에서만 사용하실 수 있습니다.");
    //   return;  
    // }

    setInitialFileContent(content);
    setSelectedTool(tool);
  };

  const handleBack = () => {
    if (selectedTool) {
      setSelectedTool(null);
      setInitialFileContent(null); // [수정] 뒤로가기 시 파일 내용 초기화
    } else {
      navigate('/');
    }
  };

  // [추가] 파일 읽기 유틸리티 함수
  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  // [추가] 드래그 오버 핸들러 (드롭 허용 및 시각적 피드백)
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };
  
  // [추가] 드래그 리브 핸들러 (시각적 피드백 제거)
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
  };

  // [추가] 드롭 핸들러 (파일 처리 로직)
  const handleDrop = (e, tool) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    if ((tool.id === 'markdown' || tool.id === 'html') && isMobileDevice) {
      setModalMessage("이 서비스는 데스크톱 PC에서만 사용하실 수 있습니다.");
      return; // 실행 중단
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      const fileName = file.name.toLowerCase();
      const isAccepted = tool.accepts.some(ext => fileName.endsWith(ext));

      if (isAccepted) {
        readFileContent(file)
          .then(content => {
            handleToolClick(tool, content); // 파일 내용과 함께 편집기 열기
          })
          .catch(error => {
            console.error("Error reading file:", error);
            alert(`파일을 읽는 중 오류가 발생했습니다: ${file.name}`);
          });
      } else {
        alert(`${tool.title}에는 ${tool.accepts.join(', ')} 파일만 열 수 있습니다. (받은 파일: ${file.name})`);
      }
    }
  };

  // 선택된 도구의 컴포넌트 렌더링
  if (selectedTool) {
    const ToolComponent = selectedTool.component;
    return (
      <ToolComponent 
        onBack={handleBack} 
        user={user} 
        userProfile={userProfile} 
        initialFileContent={initialFileContent} // [수정] 초기 파일 내용 prop 전달
      />
    );
  }

  

  return (
    <div className="utils">
      {/* [추가] 중앙 정렬 커스텀 모달 */}
      {modalMessage && (
        <div className="custom-modal-backdrop">
          <div className="custom-modal">
            <p className="custom-modal-message">{modalMessage}</p>
            <button onClick={closeModal} className="custom-modal-button">확인</button>
          </div>
        </div>
      )}
      <div className="utils-header"> 
        <div 
          onClick={handleBack}  
          className="common-back-button"
        >
          <ChevronLeft size={24} />
        </div>
        <h1>도구 모음</h1>
      </div>

      <VisitStats 
        pageName="admin-menu" 
        user={user}
        className="inline"
      />

      <div className="utils-content">
        <div className="tools-intro">
          <h2>유용한 개발 도구들</h2>
          <p>다양한 편집기와 도구를 사용하여 작업 효율을 높이세요</p>
        </div>

        <div className="tools-cards">
          {tools.map((tool) => (
            <div 
              key={tool.id} 
              className="tool-card"
              onClick={() => handleToolClick(tool)}
              // [수정] 드래그 앤 드롭 이벤트 핸들러 연결
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, tool)}
            >
              <div className="tool-icon">
                <tool.icon className="icon" />
              </div>
              <h3 className="tool-title">{tool.title}</h3>
              {!isMobileDevice && (
                <p className="tool-description">{tool.description}</p> 
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Utils;